"use client";

import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { Flame } from "lucide-react";
import { cn } from "@/lib/utils";
import { useStreak } from "@/hooks/use-streak";
import {
  trackStreakViewed,
  trackLeaderboardOpened,
} from "@/lib/analytics";
import { formatRank } from "@/lib/streak/milestones";
import { LeaderboardModal } from "@/components/streak/leaderboard-modal";

/**
 * The flame streak pill in the top nav. Mirrors CreditCounter's shape (compact
 * rounded-full pill; count-only on mobile, "N-day streak" on desktop). Self-hides
 * when there's no active streak to show, so it never nags with "0".
 *
 * This is the canonical emitter of `streak_viewed` (fired once per mount when a
 * live streak is shown) — the dashboard streak card deliberately does NOT emit it
 * again, so a page view counts as one streak impression.
 *
 * Click opens the full leaderboard (variant C) or routes to the dashboard where
 * the streak card lives (variant B).
 */
export function StreakBadge() {
  const { data } = useStreak();
  const router = useRouter();
  const [modalOpen, setModalOpen] = useState(false);
  const viewedRef = useRef(false);

  const streak = data?.currentStreak ?? 0;
  const show = !!data?.enabled && streak >= 1;

  useEffect(() => {
    if (!show || viewedRef.current || !data) return;
    viewedRef.current = true;
    trackStreakViewed({
      currentStreakDays: data.currentStreak,
      userRank: data.rank,
      planType: data.plan,
    });
  }, [show, data]);

  if (!show || !data) return null;

  const canOpenLeaderboard = data.showLeaderboard;

  function handleClick() {
    if (!data) return;
    if (canOpenLeaderboard) {
      trackLeaderboardOpened({
        userRank: data.rank,
        totalRankedUsers: data.totalRanked,
        source: "streak_badge",
      });
      setModalOpen(true);
    } else {
      router.push("/projects");
    }
  }

  const title = canOpenLeaderboard
    ? `${streak}-day building streak · ${formatRank(data.rank, data.totalRanked)} — view leaderboard`
    : `${streak}-day building streak — keep it going`;

  return (
    <>
      <button
        onClick={handleClick}
        title={title}
        aria-label={title}
        className={cn(
          "flex h-8 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-3 text-[13px] font-medium",
          // Green since PANDA-2025. The chip and StreakCard render on the same
          // screen, so leaving this one warm would have put an orange flame
          // beside a green one. The leaderboard MODAL keeps its warm flames —
          // that was PANDA-1779's call and it is out of this ticket's
          // non-modal scope.
          "text-emerald-700 transition-all duration-150 hover:bg-emerald-500/10",
          "dark:text-emerald-400 dark:hover:bg-emerald-500/15",
        )}
      >
        <Flame className="h-3.5 w-3.5 shrink-0 fill-emerald-600 text-emerald-600 dark:fill-emerald-400 dark:text-emerald-400 animate-flame-flicker" />
        <span className="sm:hidden tabular-nums">{streak}</span>
        <span className="hidden tabular-nums sm:inline">{streak}-day streak</span>
      </button>

      {canOpenLeaderboard && (
        <LeaderboardModal open={modalOpen} onOpenChange={setModalOpen} />
      )}
    </>
  );
}
